import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';

import {
  Add,
  Close,
  Agriculture,
  LocationOn,
  Business,
  Phone,
  Email,
  Verified,
  People,
  Person,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { useTranslation } from 'react-i18next';
import FarmOrganizationForm from './FarmOrganizationForm';
import { farmService, Farm } from '../../../services/farm/farmService';
import { FarmOrganizationData } from '../types/farmTypes';
import { OrganizationFormData } from '../../organizations/types/organizationTypes';
import useAuth from '../../../hooks/useAuth';

interface FarmManagementProps {
  tenantId?: string;
}

const FarmManagement: React.FC<FarmManagementProps> = ({ tenantId }) => {
  const { t } = useTranslation(['forms', 'common', 'farm']);
  const { authState } = useAuth();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const currentTenantId = tenantId || authState.user?.id || 'default_tenant';
  const isProfessionalPlan = authState.user?.planType === 'professional' || authState.planType === 'professional';

  // Load farms on component mount
  useEffect(() => {
    loadFarms();
  }, [currentTenantId]);

  const loadFarms = async () => {
    try {
      setLoading(true);
      const result = await farmService.searchByOwner(currentTenantId);
      if (result.success && result.data) {
        setFarms(result.data);
      } else {
        console.error('Failed to load farms:', result.error);
        showSnackbar(t('errors.load_farms_failed', { ns: 'farm' }), 'error');
      }
    } catch (error) {
      console.error('Error loading farms:', error);
      showSnackbar(t('errors.load_farms_failed', { ns: 'farm' }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenForm = () => {
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleFormSubmit = async (formData: FarmOrganizationData) => {
    try {
      setSubmitting(true);
      
      // Convert FarmOrganizationData to Farm format
      const farmData: Omit<Farm, 'id' | 'created_at' | 'updated_at'> = {
        owner_id: authState.user?.id || 'default_user',
        name: formData.name,
        // Ensure farm_type is an array as required by the API
        farm_type: Array.isArray(formData.farm_type)
          ? formData.farm_type
          : [formData.farm_type || 'other'],
        registration_number: formData.registration_number,
        country_code: formData.country_code || 'BR',
        location: {
          address: formData.location?.address || '',
          region: formData.location?.region || '',
          postal_code: formData.location?.postal_code || '',
          coordinates: formData.location?.coordinates || { latitude: 0, longitude: 0 }
        },
        area_hectares: formData.area_hectares || 0,
        capacity_head: formData.capacity_head || 0,
        production_focus: formData.production_focus || 'mixed_production',
        certifications: formData.certifications || [],
        contact_info: {
          phone: formData.contact_info?.phone || '',
          email: formData.contact_info?.email || '',
          emergency_contact: formData.contact_info?.emergency_contact || ''
        },
        facilities: formData.facilities || ['feed_storage', 'veterinary_facilities'],
        is_active: formData.is_active ?? true,
        is_favorite: formData.is_favorite || false
      };
      // Use createFarm to add timestamps and audit fields, and wrap payload in 'data'
      const result = await farmService.createFarm(farmData);
      
      if (result.success) {
        showSnackbar(t('success.farm_created', { ns: 'farm' }), 'success');
        setFormOpen(false);
        loadFarms(); // Reload the farms list
      } else {
        showSnackbar(result.error || t('errors.create_farm_failed', { ns: 'farm' }), 'error');
      }
    } catch (error) {
      console.error('Error creating farm:', error);
      showSnackbar(t('errors.create_farm_failed', { ns: 'farm' }), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getFarmTypeLabel = (farmType: string | string[]) => {
    const type = Array.isArray(farmType) ? farmType[0] : farmType;
    const typeMap: { [key: string]: string } = {
      'dairy': t('farm_types.dairy', { ns: 'farm' }),
      'beef': t('farm_types.beef', { ns: 'farm' }),
      'mixed': t('farm_types.mixed', { ns: 'farm' }),
      'other': t('farm_types.other', { ns: 'farm' })
    };
    return typeMap[type] || type;
  };

  const getManagerTypeLabel = (managerType: string) => {
    const typeMap: { [key: string]: string } = {
      'gestor_nutricao': t('manager_types.nutrition', { ns: 'farm' }),
      'gestor_pasto': t('manager_types.pasture', { ns: 'farm' }),
      'gestor_animal': t('manager_types.animals', { ns: 'farm' }),
      'gestor_lote': t('manager_types.lots', { ns: 'farm' }),
      'gestor_financeiro': t('manager_types.financial', { ns: 'farm' }),
      'gestor_nascimento': t('manager_types.birth', { ns: 'farm' })
    };
    return typeMap[managerType] || managerType;
  };

  const renderFarmCard = (farm: Farm) => (
    <Card 
      key={farm.id || farm.owner_id} 
      sx={{ 
        mb: 2, 
        '&:hover': { 
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm, mr: 2 }}>
            <Agriculture />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm }}>
              {farm.name}
            </Typography>
            {farm.registration_number && (
              <Typography variant="body2" color="text.secondary">
                {t('labels.registration', { ns: 'farm' })}: {farm.registration_number}
              </Typography>
            )}
          </Box>
          <Chip 
            label={getFarmTypeLabel(farm.farm_type)}
            size="small"
            sx={{ 
              backgroundColor: 'rgba(107, 196, 60, 0.12)',
              color: DESIGN_TOKENS.colors.primary[700]
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationOn sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary, mr: 1 }} />
              <Typography variant="body2">
                {farm.location?.address || t('labels.address_not_provided', { ns: 'farm' })}
              </Typography>
            </Box>
            {farm.location?.region && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                {farm.location.region}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ flex: 1 }}>
            {farm.area_hectares && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Business sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary, mr: 1 }} />
                <Typography variant="body2">
                  {t('labels.area', { ns: 'farm' })}: {farm.area_hectares} {t('labels.hectares', { ns: 'farm' })}
                </Typography>
              </Box>
            )}
            {farm.capacity_head && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <img 
                  src="/vaca.svg" 
                  alt="Farm" 
                  style={{ 
                    width: 16, 
                    height: 16, 
                    marginRight: 8,
                    filter: 'brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(102%) contrast(102%)'
                  }} 
                />
                <Typography variant="body2">
                  {t('labels.capacity', { ns: 'farm' })}: {farm.capacity_head} {t('labels.heads', { ns: 'farm' })}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {farm.contact_info && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              {farm.contact_info.phone && (
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Phone sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary, mr: 1 }} />
                    <Typography variant="body2">
                      {farm.contact_info.phone}
                    </Typography>
                  </Box>
                </Box>
              )}
              {farm.contact_info.email && (
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Email sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary, mr: 1 }} />
                    <Typography variant="body2">
                      {farm.contact_info.email}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {farm.certifications && farm.certifications.length > 0 && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
              {t('labels.certifications', { ns: 'farm' })}:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {farm.certifications.map((cert, index) => (
                <Chip
                  key={index}
                  label={cert}
                  size="small"
                  icon={<Verified />}
                  sx={{ 
                    fontSize: '0.75rem',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    color: DESIGN_TOKENS.colors.status.success[500]
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        
        {/* Collaborators Section */}
        {farm.collaborators && farm.collaborators.length > 0 && (
          <Box sx={{ width: '100%', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
              <People sx={{ fontSize: 16, mr: 1 }} />
              {t('labels.collaborators', { ns: 'farm' })} ({farm.collaborators.length})
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {farm.collaborators.slice(0, 3).map((collaborator, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ width: 24, height: 24, mr: 1, fontSize: '0.75rem' }}>
                      <Person sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {collaborator.user_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {collaborator.role === 'manager' ? t('labels.manager', { ns: 'farm' }) : collaborator.role}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Manager Assignments */}
                  {collaborator.manager_assignments && collaborator.manager_assignments.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: '60%' }}>
                      {collaborator.manager_assignments.slice(0, 2).map((assignment, assignIndex) => (
                        <Chip
                          key={assignIndex}
                          label={getManagerTypeLabel(assignment.manager_type)}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            backgroundColor: 'rgba(107, 196, 60, 0.12)',
                            color: DESIGN_TOKENS.colors.organization.farm,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      ))}
                      {collaborator.manager_assignments.length > 2 && (
                        <Chip
                          label={`+${collaborator.manager_assignments.length - 2}`}
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            backgroundColor: 'rgba(158, 158, 158, 0.1)',
                            color: 'text.secondary',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              ))}
              
              {farm.collaborators.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                  +{farm.collaborators.length - 3} {t('labels.more_collaborators', { ns: 'farm' })}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const initialFormData: OrganizationFormData = {
    name: '',
    type: 'farm',
    description: ''
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm, mb: 1 }}>
          {t('titles.farm_management', { ns: 'farm' })}
        </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('descriptions.farm_management', { ns: 'farm' })}
          </Typography>
        </Box>
        {!isProfessionalPlan && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenForm}
            sx={{
                backgroundColor: DESIGN_TOKENS.colors.organization.farm,
                '&:hover': { backgroundColor: DESIGN_TOKENS.colors.primary[600] }
              }}
          >
            {t('buttons.new_farm', { ns: 'farm' })}
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Farms List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: DESIGN_TOKENS.colors.organization.farm }} />
        </Box>
      ) : farms.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Agriculture sx={{ fontSize: 64, color: DESIGN_TOKENS.colors.text.disabled, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            {t('empty_states.no_farms', { ns: 'farm' })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('empty_states.add_first_farm', { ns: 'farm' })}
          </Typography>
          {!isProfessionalPlan && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenForm}
              sx={{
                backgroundColor: DESIGN_TOKENS.colors.organization.farm,
                '&:hover': { backgroundColor: DESIGN_TOKENS.colors.primary[600] }
              }}
            >
              {t('buttons.add_farm', { ns: 'farm' })}
            </Button>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 3, color: DESIGN_TOKENS.colors.text.primary }}>
          {t('titles.registered_farms', { ns: 'farm' })} ({farms.length})
        </Typography>
          {farms.map(renderFarmCard)}
        </Box>
      )}

      {/* Form Dialog */}
      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.organization.farm, fontWeight: 600 }}>
            {t('titles.new_farm', { ns: 'farm' })}
          </Typography>
          <IconButton onClick={handleCloseForm} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <FarmOrganizationForm
            initialData={initialFormData}
            onSubmit={handleFormSubmit}
            onBack={handleCloseForm}
            isLoading={submitting}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FarmManagement;